<template>
  <div>
    <!-- Toolbar -->
    <OutputTimeSeriesToolbar
      :settings="toolbarSettings"
      :dates="dates"
      :resources-items="resourcesItems"
      page="pf"
      :loading="loading"
      @update:settings="updateToolbarSettings"
      @update:dates="updateDates"
    />

    <div v-if="!loading.value">
      <!-- Date Filter Alert -->
      <v-alert v-if="showDateFilterAlert" type="warning" class="mb-4">
        No data found for selected date range. Showing data for {{ fallbackDateDisplay }} instead.
      </v-alert>

      <v-card class="mb-4">
        <v-card-title>Power Flow Dashboard</v-card-title>
        <v-card-text>
          <!-- Contingency Analysis View -->
          <div v-if="toolbarSettings.viewMode === 'contingency'">
            <h2 class="mb-4">Contingency Analysis</h2>
            <v-data-table
              :headers="contingencyHeaders"
              :items="contingencyData"
              :items-per-page="20"
              class="elevation-1"
              dense
            >
              <template v-slot:item.maxVm="{ item }">
                {{ formatTableValue(item.maxVm) }}
              </template>
              <template v-slot:item.minVm="{ item }">
                {{ formatTableValue(item.minVm) }}
              </template>
              <template v-slot:item.maxLoading="{ item }">
                {{ formatTableValue(item.maxLoading) }}
              </template>
              <template v-slot:item.loss="{ item }">
                {{ formatTableValue(item.loss) }}
              </template>
            </v-data-table>
          </div>

          <!-- Violation Report View -->
          <div v-else-if="toolbarSettings.viewMode === 'violationReport'">
            <!-- Node Violations Table -->
            <div class="mb-6">
              <PFViolationReport
                :upperLimit="voltageUpperLimit"
                :lowerLimit="voltageLowerLimit"
                :data="nodeViolationTableData"
                units="p.u."
                title="Bus Voltage Violations"
              />
            </div>

            <!-- Line Loading Violations Table -->
            <div class="mb-6">
              <PFViolationReport
                :upperLimit="lineLoadingLimit"
                :data="lineViolationTableData"
                units="%"
                title="Line Loading Violations"
              />
            </div>

            <!-- Transformer Loading Violations Table -->
            <div v-if="resourcesItems.trafo.length > 0" class="mb-6">
              <PFViolationReport
                :upperLimit="trafoLoadingLimit"
                :data="trafoViolationTableData"
                units="%"
                title="Transformer Loading Violations"
              />
            </div>

            <!-- Generator Power Factor Violations Table -->
            <div v-if="resourcesItems.gen.length > 0" class="mb-6">
              <PFViolationReport
                :lowerLimit="genPowerFactorLimit"
                :data="genViolationTableData"
                units=""
                title="Generator Power Factor Violations"
              />
            </div>
          </div>

          <!-- Tabular View -->
          <div v-else-if="toolbarSettings.pfTimeSeriesOutputType === 'tabular'">
            <!-- Buses Tables Section -->
            <div v-if="resourcesItems.bus.length > 0" class="mb-8">
              <h2 class="mb-4">Bus Tables</h2>

              <v-tabs v-model="busActiveTab" class="mb-4">
                <v-tab v-for="metric in busMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="busActiveTab">
                <v-tab-item v-for="metric in busMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getBusTableHeaders()"
                    :items="getBusTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.busName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- Lines Tables Section -->
            <div v-if="resourcesItems.line.length > 0" class="mb-8">
              <h2 class="mb-4">Line Tables</h2>

              <v-tabs v-model="lineActiveTab" class="mb-4">
                <v-tab v-for="metric in lineMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="lineActiveTab">
                <v-tab-item v-for="metric in lineMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getLineTableHeaders()"
                    :items="getLineTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.lineName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- 2-Winding Transformers Tables Section -->
            <div v-if="resourcesItems.trafo.length > 0" class="mb-8">
              <h2 class="mb-4">2-Winding Transformer Tables</h2>

              <v-tabs v-model="trafoActiveTab" class="mb-4">
                <v-tab v-for="metric in trafoMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="trafoActiveTab">
                <v-tab-item v-for="metric in trafoMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getTrafoTableHeaders()"
                    :items="getTrafoTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.trafoName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- 3-Winding Transformers Tables Section -->
            <div v-if="resourcesItems.trafo3w.length > 0" class="mb-8">
              <h2 class="mb-4">3-Winding Transformer Tables</h2>

              <v-tabs v-model="trafo3wActiveTab" class="mb-4">
                <v-tab v-for="metric in trafo3wMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="trafo3wActiveTab">
                <v-tab-item v-for="metric in trafo3wMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getTrafo3wTableHeaders()"
                    :items="getTrafo3wTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.trafo3wName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- Loads Tables Section -->
            <div v-if="resourcesItems.load.length > 0" class="mb-8">
              <h2 class="mb-4">Load Tables</h2>

              <v-tabs v-model="loadActiveTab" class="mb-4">
                <v-tab v-for="metric in loadMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="loadActiveTab">
                <v-tab-item v-for="metric in loadMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getLoadTableHeaders()"
                    :items="getLoadTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.loadName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- Generators Tables Section -->
            <div v-if="resourcesItems.gen.length > 0" class="mb-8">
              <h2 class="mb-4">Generator Tables</h2>

              <v-tabs v-model="genActiveTab" class="mb-4">
                <v-tab v-for="metric in genMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="genActiveTab">
                <v-tab-item v-for="metric in genMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getGenTableHeaders()"
                    :items="getGenTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.genName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- Static Generators Tables Section -->
            <div v-if="resourcesItems.sgen.length > 0" class="mb-8">
              <h2 class="mb-4">Static Generator Tables</h2>

              <v-tabs v-model="sgenActiveTab" class="mb-4">
                <v-tab v-for="metric in sgenMetrics" :key="metric.key">
                  {{ metric.name }}
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="sgenActiveTab">
                <v-tab-item v-for="metric in sgenMetrics" :key="metric.key">
                  <v-data-table
                    :headers="getSgenTableHeaders()"
                    :items="getSgenTableData(metric.key)"
                    :items-per-page="20"
                    class="elevation-1"
                    dense
                  >
                    <template v-slot:item="{ item }">
                      <tr>
                        <td class="font-weight-bold">{{ item.sgenName }}</td>
                        <td v-for="timeValue in item.values" :key="timeValue.time">
                          {{ formatTableValue(timeValue.value) }}
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-tab-item>
              </v-tabs-items>
            </div>
          </div>

          <!-- Chart View -->
          <div v-else>
            <!-- No Elements Selected Alert -->
            <v-alert v-if="totalSelectedElements === 0" type="info" class="mb-4">
              Select elements from the toolbar above to display power flow charts
            </v-alert>

            <!-- Buses (nodes) Section -->
            <div v-if="toolbarSettings.bus.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>Buses</h2>
                </v-col>
                <v-col v-for="metric in busMetrics.slice(0, 4)" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getBusMetricData(metric.key)"
                    :layout="getBusMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>

              <!-- Additional Bus Charts (Collapsible) -->
              <v-expansion-panels v-if="busMetrics.length > 4" class="mt-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span>Additional Bus Charts ({{ busMetrics.length - 4 }} more)</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col v-for="metric in busMetrics.slice(4)" :key="metric.key" cols="6">
                        <h3>{{ metric.name }}</h3>
                        <Plotly
                          :data="getBusMetricData(metric.key)"
                          :layout="getBusMetricLayout(metric.label)"
                          :config="{ responsive: true }"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <!-- 2-Winding Transformers Section -->
            <div v-if="toolbarSettings.trafo.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>2-Winding Transformers</h2>
                </v-col>
                <v-col v-for="metric in trafoMetrics.slice(0, 4)" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getTrafoMetricData(metric.key)"
                    :layout="getTrafoMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>

              <!-- Additional 2W Transformer Charts (Collapsible) -->
              <v-expansion-panels v-if="trafoMetrics.length > 4" class="mt-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span
                      >Additional 2W Transformer Charts ({{ trafoMetrics.length - 4 }} more)</span
                    >
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col v-for="metric in trafoMetrics.slice(4)" :key="metric.key" cols="6">
                        <h3>{{ metric.name }}</h3>
                        <Plotly
                          :data="getTrafoMetricData(metric.key)"
                          :layout="getTrafoMetricLayout(metric.label)"
                          :config="{ responsive: true }"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <!-- Lines Section -->
            <div v-if="toolbarSettings.line.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>Lines</h2>
                </v-col>
                <v-col v-for="metric in lineMetrics.slice(0, 4)" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getLineMetricData(metric.key)"
                    :layout="getLineMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>

              <!-- Additional Line Charts (Collapsible) -->
              <v-expansion-panels v-if="lineMetrics.length > 4" class="mt-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span>Additional Line Charts ({{ lineMetrics.length - 4 }} more)</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col v-for="metric in lineMetrics.slice(4)" :key="metric.key" cols="6">
                        <h3>{{ metric.name }}</h3>
                        <Plotly
                          :data="getLineMetricData(metric.key)"
                          :layout="getLineMetricLayout(metric.label)"
                          :config="{ responsive: true }"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>

            <!-- Loads Section -->
            <div v-if="toolbarSettings.load.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>Loads</h2>
                </v-col>
                <v-col v-for="metric in loadMetrics" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getLoadMetricData(metric.key)"
                    :layout="getLoadMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Generators Section -->
            <div v-if="toolbarSettings.gen.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>Generators</h2>
                </v-col>
                <v-col v-for="metric in genMetrics" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getGenMetricData(metric.key)"
                    :layout="getGenMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- Static Generators Section -->
            <div v-if="toolbarSettings.sgen.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>Static Generators</h2>
                </v-col>
                <v-col v-for="metric in sgenMetrics" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getSgenMetricData(metric.key)"
                    :layout="getSgenMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>
            </div>

            <!-- 3-Winding Transformers Section -->
            <div v-if="toolbarSettings.trafo3w.length > 0">
              <v-row>
                <v-col cols="12">
                  <h2>3-Winding Transformers</h2>
                </v-col>
                <v-col v-for="metric in trafo3wMetrics.slice(0, 4)" :key="metric.key" cols="6">
                  <h3>{{ metric.name }}</h3>
                  <Plotly
                    :data="getTrafo3wMetricData(metric.key)"
                    :layout="getTrafo3wMetricLayout(metric.label)"
                    :config="{ responsive: true }"
                  />
                </v-col>
              </v-row>

              <!-- Additional 3W Transformer Charts (Collapsible) -->
              <v-expansion-panels v-if="trafo3wMetrics.length > 4" class="mt-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span
                      >Additional 3W Transformer Charts ({{ trafo3wMetrics.length - 4 }} more)</span
                    >
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col v-for="metric in trafo3wMetrics.slice(4)" :key="metric.key" cols="6">
                        <h3>{{ metric.name }}</h3>
                        <Plotly
                          :data="getTrafo3wMetricData(metric.key)"
                          :layout="getTrafo3wMetricLayout(metric.label)"
                          :config="{ responsive: true }"
                        />
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import OutputTimeSeriesToolbar from "@/components/OutputTimeSeriesToolbar.vue";
import Plotly from "@/components/charts/Plotly.vue";
import PFViolationReport from "@/components/PFViolationReport.vue";
import { DateTime } from "luxon";
import Zip from "jszip";
import Papa from "papaparse";
import download from "downloadjs";

export default {
  name: "PowerFlowDashboard",
  components: {
    OutputTimeSeriesToolbar,
    Plotly,
    PFViolationReport,
  },
  props: {
    outputs: { type: Array, required: true },
    currentCase: { type: Object, required: true },
  },
  data() {
    return {
      loading: { value: false, text: "" },
      toolbarSettings: {
        pfTimeSeriesOutputType: "dashboard",
        year: null,
        outputSummary: true,
        viewMode: "timeSeries",
        timeInterval: 60,
        hasTimeseries: true,
        hasContingency: false,
        // Element selections
        bus: [],
        line: [],
        trafo: [],
        trafo3w: [],
        load: [],
        gen: [],
        sgen: [],
      },
      dates: [],
      resourcesItems: {
        // each one of these have { text: string, value: number }
        // where the value is the index of the outputs
        bus: [],
        line: [],
        trafo: [],
        trafo3w: [],
        load: [],
        gen: [],
        sgen: [],
      },
      // Tab states for tabular view
      busActiveTab: 0,
      trafoActiveTab: 0,
      trafo3wActiveTab: 0,
      lineActiveTab: 0,
      loadActiveTab: 0,
      genActiveTab: 0,
      sgenActiveTab: 0,
    };
  },

  computed: {
    contingencyData() {
      if (!this.outputs || this.outputs.length === 0) return [];
      const contingencyObj = this.outputs.find((obj) => obj.contingencies);
      if (!contingencyObj || !contingencyObj.contingencies) return [];

      return contingencyObj.contingencies.map((item, index) => ({
        id: `C-${index}`,
        element: item.summary["N-1 Element Outage"],
        converge: item.summary["Power Flow Converge"],
        status: item.summary.Status,
        maxVm: item.summary["Max Voltage (p.u.)"],
        minVm: item.summary["Min Voltage (p.u.)"],
        maxLoading: item.summary["Max Line Loading (%)"],
        loss: item.summary["Total System Loss (MW)"],
        violations: item.summary["Number of Violations"],
      }));
    },

    contingencyHeaders() {
      return [
        { text: "ID", value: "id" },
        { text: "N-1 Element Outage", value: "element" },
        { text: "Converged", value: "converge" },
        { text: "Status", value: "status" },
        { text: "Max Voltage (p.u.)", value: "maxVm" },
        { text: "Min Voltage (p.u.)", value: "minVm" },
        { text: "Max Line Loading (%)", value: "maxLoading" },
        { text: "Total Loss (MW)", value: "loss" },
        { text: "Violations", value: "violations" },
      ];
    },

    filteredOutputs() {
      if (!this.outputs || this.outputs.length === 0) {
        return [];
      }

      if (!this.dates || this.dates.length < 2 || !this.dates[0] || !this.dates[1]) {
        return this.outputs;
      }

      const startDate = DateTime.fromISO(this.dates[0]).startOf("day");
      const endDate = DateTime.fromISO(this.dates[1]).endOf("day");

      const filtered = this.outputs.filter((timePoint) => {
        const pointDate = DateTime.fromISO(timePoint.datetime);
        return pointDate >= startDate && pointDate <= endDate;
      });

      // Edge case: No data in range, show first day only
      if (filtered.length === 0) {
        const firstDay = DateTime.fromISO(this.outputs[0].datetime).toISODate();
        return this.outputs.filter((timePoint) => {
          const pointDate = DateTime.fromISO(timePoint.datetime).toISODate();
          return pointDate === firstDay;
        });
      }

      return filtered;
    },

    showDateFilterAlert() {
      if (!this.outputs || this.outputs.length === 0) {
        return false;
      }

      if (!this.dates || this.dates.length < 2 || !this.dates[0] || !this.dates[1]) {
        return false;
      }

      const startDate = DateTime.fromISO(this.dates[0]).startOf("day");
      const endDate = DateTime.fromISO(this.dates[1]).endOf("day");

      const filtered = this.outputs.filter((timePoint) => {
        const pointDate = DateTime.fromISO(timePoint.datetime);
        return pointDate >= startDate && pointDate <= endDate;
      });

      return filtered.length === 0;
    },

    fallbackDateDisplay() {
      if (!this.outputs || this.outputs.length === 0) {
        return "";
      }
      return DateTime.fromISO(this.outputs[0].datetime).toFormat("yyyy-MM-dd");
    },

    // Define metrics for each element type with human-readable names
    busMetrics() {
      return [
        { key: "vm_pu", name: "Voltage Magnitude (p.u.)", label: "Voltage Magnitude (p.u)" },
        { key: "va_degree", name: "Voltage Angle (°)", label: "Voltage Angle (degree)" },
        { key: "p_mw", name: "Active Power (MW)", label: "Active Power (MW)" },
        { key: "q_mvar", name: "Reactive Power (MVar)", label: "Reactive Power (MVar)" },
      ];
    },

    trafoMetrics() {
      return [
        { key: "loading_percent", name: "Loading (%)", label: "Loading (%)" },
        { key: "p_hv_mw", name: "HV Active Power (MW)", label: "Active Power HV (MW)" },
        { key: "q_hv_mvar", name: "HV Reactive Power (MVar)", label: "Reactive Power HV (MVar)" },
        { key: "p_lv_mw", name: "LV Active Power (MW)", label: "Active Power LV (MW)" },
        { key: "q_lv_mvar", name: "LV Reactive Power (MVar)", label: "Reactive Power LV (MVar)" },
      ];
    },

    lineMetrics() {
      return [
        { key: "loading_percent", name: "Loading (%)", label: "Loading (%)" },
        { key: "p_from_mw", name: "Active Power From (MW)", label: "Active Power From (MW)" },
        {
          key: "q_from_mvar",
          name: "Reactive Power From (MVar)",
          label: "Reactive Power From (MVar)",
        },
        { key: "p_to_mw", name: "Active Power To (MW)", label: "Active Power To (MW)" },
        { key: "q_to_mvar", name: "Reactive Power To (MVar)", label: "Reactive Power To (MVar)" },
        { key: "pl_mw", name: "Power Losses (MW)", label: "Power Losses (MW)" },
        { key: "ql_mvar", name: "Reactive Losses (MVar)", label: "Reactive Losses (MVar)" },
        { key: "i_from_ka", name: "Current From (kA)", label: "Current From (kA)" },
        { key: "i_to_ka", name: "Current To (kA)", label: "Current To (kA)" },
        { key: "i_ka", name: "Current Max (kA)", label: "Current Max (kA)" },
        { key: "vm_from_pu", name: "Voltage From (p.u.)", label: "Voltage From (p.u)" },
        {
          key: "va_from_degree",
          name: "Voltage Angle From (°)",
          label: "Voltage Angle From (degree)",
        },
        { key: "vm_to_pu", name: "Voltage To (p.u.)", label: "Voltage To (p.u)" },
        { key: "va_to_degree", name: "Voltage Angle To (°)", label: "Voltage Angle To (degree)" },
      ];
    },

    loadMetrics() {
      return [
        { key: "p_mw", name: "Active Power (MW)", label: "Active Power (MW)" },
        { key: "q_mvar", name: "Reactive Power (MVar)", label: "Reactive Power (MVar)" },
      ];
    },

    genMetrics() {
      return [
        { key: "p_mw", name: "Active Power (MW)", label: "Active Power (MW)" },
        { key: "q_mvar", name: "Reactive Power (MVar)", label: "Reactive Power (MVar)" },
        { key: "power_factor", name: "Power Factor", label: "Power Factor" },
        { key: "vm_pu", name: "Voltage Magnitude (p.u.)", label: "Voltage Magnitude (p.u)" },
        { key: "va_degree", name: "Voltage Angle (°)", label: "Voltage Angle (degree)" },
      ];
    },

    sgenMetrics() {
      return [
        { key: "p_mw", name: "Active Power (MW)", label: "Active Power (MW)" },
        { key: "q_mvar", name: "Reactive Power (MVar)", label: "Reactive Power (MVar)" },
      ];
    },

    trafo3wMetrics() {
      return [
        { key: "loading_percent", name: "Loading (%)", label: "Loading (%)" },
        { key: "p_hv_mw", name: "HV Active Power (MW)", label: "Active Power HV (MW)" },
        { key: "q_hv_mvar", name: "HV Reactive Power (MVar)", label: "Reactive Power HV (MVar)" },
        { key: "p_mv_mw", name: "MV Active Power (MW)", label: "Active Power MV (MW)" },
        { key: "q_mv_mvar", name: "MV Reactive Power (MVar)", label: "Reactive Power MV (MVar)" },
        { key: "p_lv_mw", name: "LV Active Power (MW)", label: "Active Power LV (MW)" },
        { key: "q_lv_mvar", name: "LV Reactive Power (MVar)", label: "Reactive Power LV (MVar)" },
      ];
    },

    totalSelectedElements() {
      return (
        this.toolbarSettings.bus.length +
        this.toolbarSettings.line.length +
        this.toolbarSettings.trafo.length +
        this.toolbarSettings.trafo3w.length +
        this.toolbarSettings.load.length +
        this.toolbarSettings.gen.length +
        this.toolbarSettings.sgen.length
      );
    },

    voltageUpperLimit() {
      const percentageValue =
        this.currentCase?.settings?.POWER_FLOW?.node_upper_threshold_voltage || 105;
      return percentageValue / 100; // Convert percentage to p.u.
    },

    voltageLowerLimit() {
      const percentageValue =
        this.currentCase?.settings?.POWER_FLOW?.node_lower_threshold_voltage || 95;
      return percentageValue / 100; // Convert percentage to p.u.
    },

    lineLoadingLimit() {
      return this.currentCase?.settings?.POWER_FLOW?.line_percent_rating || 95;
    },

    trafoLoadingLimit() {
      return this.currentCase?.settings?.POWER_FLOW?.trafo_percent_rating || 95;
    },

    genPowerFactorLimit() {
      return this.currentCase?.settings?.POWER_FLOW?.gen_power_factor_threshold || 0.9;
    },

    nodeViolationTableData() {
      if (!this.outputs || this.outputs.length === 0 || !this.resourcesItems.bus.length) {
        return [{ Node: "No data" }];
      }

      // Analyze each bus for voltage violations
      const busViolationData = this.resourcesItems.bus.map(({ text: busName }, busIndex) => {
        const row = [["Node", busName]];

        this.filteredOutputs.forEach((timePoint) => {
          const pointValues = timePoint.value;
          const voltageValue = pointValues.bus[busIndex].vm_pu;
          const formattedTime = this.formatTime(timePoint.datetime);

          row.push([formattedTime, voltageValue]);
        });

        return Object.fromEntries(row);
      });

      return busViolationData;
    },

    lineViolationTableData() {
      if (!this.outputs || this.outputs.length === 0 || !this.resourcesItems.line.length) {
        return [{ Line: "No data" }];
      }

      // Analyze each line for loading violations
      const lineViolationData = this.resourcesItems.line.map(({ text: lineName }, lineIndex) => {
        const row = [["Line", lineName]];

        this.filteredOutputs.forEach((timePoint) => {
          const pointValues = timePoint.value;
          const loadingValue = pointValues.line[lineIndex].loading_percent;
          const formattedTime = this.formatTime(timePoint.datetime);

          row.push([formattedTime, loadingValue]);
        });

        return Object.fromEntries(row);
      });

      return lineViolationData;
    },

    trafoViolationTableData() {
      if (!this.outputs || this.outputs.length === 0 || !this.resourcesItems.trafo.length) {
        return [{ Transformer: "No data" }];
      }

      // Analyze each transformer for loading violations
      const trafoViolationData = this.resourcesItems.trafo.map(
        ({ text: trafoName }, trafoIndex) => {
          const row = [["Transformer", trafoName]];

          this.filteredOutputs.forEach((timePoint) => {
            const pointValues = timePoint.value;
            const loadingValue = pointValues.trafo[trafoIndex].loading_percent;
            const formattedTime = this.formatTime(timePoint.datetime);

            row.push([formattedTime, loadingValue]);
          });

          return Object.fromEntries(row);
        }
      );

      return trafoViolationData;
    },

    genViolationTableData() {
      if (!this.outputs || this.outputs.length === 0 || !this.resourcesItems.gen.length) {
        return [{ Generator: "No data" }];
      }

      const genViolationData = this.resourcesItems.gen.map(({ text: genName }, genIndex) => {
        const row = [["Generator", genName]];

        this.filteredOutputs.forEach((timePoint) => {
          const pointValues = timePoint.value;
          const pMw = pointValues.gen[genIndex].p_mw || 0;
          const qMvar = pointValues.gen[genIndex].q_mvar || 0;
          const apparent = Math.sqrt(pMw * pMw + qMvar * qMvar);
          const powerFactor = apparent > 0 ? Math.abs(pMw) / apparent : 0;
          const formattedTime = this.formatTime(timePoint.datetime);

          row.push([formattedTime, powerFactor]);
        });

        return Object.fromEntries(row);
      });

      return genViolationData;
    },
  },

  watch: {
    outputs: {
      handler() {
        // Reinitialize when data changes
        this.initializeData();
      },
      immediate: false,
    },
  },

  created() {
    this.initializeData();
  },

  methods: {
    formatTime(date) {
      const dt = DateTime.fromISO(date, { zone: "utc" });
      return dt.toFormat("HH:mm");
    },

    initializeData() {
      // Check for contingency data
      const contingencyObj = this.outputs.find((obj) => obj.contingencies);
      this.toolbarSettings.hasContingency = !!(
        contingencyObj &&
        contingencyObj.contingencies &&
        contingencyObj.contingencies.length > 0
      );

      // Initialize dates from power flow settings
      if (this.currentCase?.settings?.POWER_FLOW) {
        const pfSettings = this.currentCase.settings.POWER_FLOW;
        if (pfSettings.start_datetime && pfSettings.end_datetime) {
          this.dates = [
            DateTime.fromISO(pfSettings.start_datetime).toISODate(),
            DateTime.fromISO(pfSettings.end_datetime).toISODate(),
          ];
        }

        if (pfSettings.time_interval) {
          this.toolbarSettings.timeInterval = pfSettings.time_interval;
        }
      }

      // Initialize resource items from outputs
      if (!this.outputs || this.outputs.length === 0) {
        // Reset items if no outputs
        Object.keys(this.resourcesItems).forEach((key) => {
          this.resourcesItems[key] = [];
        });
        return;
      }

      const firstValue = this.outputs[0].value;

      const mapKeysToItems = (obj) => {
        if (!obj) return [];
        return Object.keys(obj)
          .sort()
          .map((name) => ({
            value: name,
            text: name,
          }));
      };

      this.resourcesItems.bus = mapKeysToItems(firstValue.bus);
      this.resourcesItems.line = mapKeysToItems(firstValue.line);
      this.resourcesItems.trafo = mapKeysToItems(firstValue.trafo);
      this.resourcesItems.trafo3w = mapKeysToItems(firstValue.trafo3w);
      this.resourcesItems.load = mapKeysToItems(firstValue.load);
      this.resourcesItems.gen = mapKeysToItems(firstValue.gen);
      this.resourcesItems.sgen = mapKeysToItems(firstValue.sgen);
    },

    updateToolbarSettings(newSettings) {
      this.toolbarSettings = { ...this.toolbarSettings, ...newSettings };
    },

    updateDates(newDates) {
      this.dates = [...newDates];
    },

    // Chart data methods for bus metrics
    getBusMetricData(metric) {
      if (this.toolbarSettings.bus.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.bus.map((busIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (timePoint.value.bus && timePoint.value.bus[busIndex]) {
            return timePoint.value.bus[busIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: busIndex,
      }));
    },

    getBusMetricLayout(metricLabel) {
      return {
        title: `Bus ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Chart data methods for 2-winding transformer metrics
    getTrafoMetricData(metric) {
      if (this.toolbarSettings.trafo.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.trafo.map((trafoIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (timePoint.value.trafo && timePoint.value.trafo[trafoIndex]) {
            return timePoint.value.trafo[trafoIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: trafoIndex,
      }));
    },

    getTrafoMetricLayout(metricLabel) {
      return {
        title: `2W Transformer ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Chart data methods for 3-winding transformer metrics
    getTrafo3wMetricData(metric) {
      if (this.toolbarSettings.trafo3w.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.trafo3w.map((trafoIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (timePoint.value.trafo3w && timePoint.value.trafo3w[trafoIndex]) {
            return timePoint.value.trafo3w[trafoIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: trafoIndex,
      }));
    },

    getTrafo3wMetricLayout(metricLabel) {
      return {
        title: `3W Transformer ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Chart data methods for line metrics
    getLineMetricData(metric) {
      if (this.toolbarSettings.line.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.line.map((lineIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (timePoint.value.line && timePoint.value.line[lineIndex]) {
            return timePoint.value.line[lineIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: lineIndex,
      }));
    },

    getLineMetricLayout(metricLabel) {
      return {
        title: `Line ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Chart data methods for load metrics
    getLoadMetricData(metric) {
      if (this.toolbarSettings.load.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.load.map((loadIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (timePoint.value.load && timePoint.value.load[loadIndex]) {
            return timePoint.value.load[loadIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: loadIndex,
      }));
    },

    getLoadMetricLayout(metricLabel) {
      return {
        title: `Load ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Chart data methods for generator metrics
    getGenMetricData(metric) {
      if (this.toolbarSettings.gen.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.gen.map((genIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (metric === "power_factor") {
            const pMw = timePoint.value.gen?.[genIndex]?.p_mw || 0;
            const qMvar = timePoint.value.gen?.[genIndex]?.q_mvar || 0;
            const apparent = Math.sqrt(pMw * pMw + qMvar * qMvar);
            return apparent > 0 ? Math.abs(pMw) / apparent : 0;
          }
          if (timePoint.value.gen && timePoint.value.gen[genIndex]) {
            return timePoint.value.gen[genIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: genIndex,
      }));
    },

    getGenMetricLayout(metricLabel) {
      return {
        title: `Generator ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Chart data methods for static generator metrics
    getSgenMetricData(metric) {
      if (this.toolbarSettings.sgen.length === 0 || this.filteredOutputs.length === 0) return [];

      return this.toolbarSettings.sgen.map((sgenIndex) => ({
        x: this.filteredOutputs.map((timePoint) => this.formatTime(timePoint.datetime)),
        y: this.filteredOutputs.map((timePoint) => {
          if (timePoint.value.sgen && timePoint.value.sgen[sgenIndex]) {
            return timePoint.value.sgen[sgenIndex][metric];
          }
          return null;
        }),
        type: "scatter",
        mode: "lines+markers",
        name: sgenIndex,
      }));
    },

    getSgenMetricLayout(metricLabel) {
      return {
        title: `Static Generator ${metricLabel} `,
        xaxis: { title: "Time" },
        yaxis: { title: metricLabel },
        height: 400,
      };
    },

    // Table data methods
    formatTableValue(value) {
      if (value === null || value === undefined) return "-";
      if (typeof value === "number") {
        return value.toFixed(3);
      }
      return value;
    },

    // Bus table methods
    getBusTableHeaders() {
      const headers = [{ text: "Bus Name", value: "busName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getBusTableData(metric) {
      return this.resourcesItems.bus.map((busItem) => {
        const values = this.filteredOutputs.map((timePoint) => ({
          time: timePoint.datetime,
          value: timePoint.value.bus?.[busItem.value]?.[metric],
        }));
        return {
          busName: busItem.text,
          values,
        };
      });
    },

    // 2-Winding Transformer table methods
    getTrafoTableHeaders() {
      const headers = [{ text: "Transformer Name", value: "trafoName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getTrafoTableData(metric) {
      return this.resourcesItems.trafo.map((trafoItem) => {
        const values = this.filteredOutputs.map((timePoint) => ({
          time: timePoint.datetime,
          value: timePoint.value.trafo?.[trafoItem.value]?.[metric],
        }));
        return {
          trafoName: trafoItem.text,
          values,
        };
      });
    },

    // 3-Winding Transformer table methods
    getTrafo3wTableHeaders() {
      const headers = [{ text: "3W Transformer Name", value: "trafo3wName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getTrafo3wTableData(metric) {
      return this.resourcesItems.trafo3w.map((trafoItem) => {
        const values = this.filteredOutputs.map((timePoint) => ({
          time: timePoint.datetime,
          value: timePoint.value.trafo3w?.[trafoItem.value]?.[metric],
        }));
        return {
          trafo3wName: trafoItem.text,
          values,
        };
      });
    },

    // Line table methods
    getLineTableHeaders() {
      const headers = [{ text: "Line Name", value: "lineName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getLineTableData(metric) {
      return this.resourcesItems.line.map((lineItem) => {
        const values = this.filteredOutputs.map((timePoint) => ({
          time: timePoint.datetime,
          value: timePoint.value.line?.[lineItem.value]?.[metric],
        }));
        return {
          lineName: lineItem.text,
          values,
        };
      });
    },

    // Load table methods
    getLoadTableHeaders() {
      const headers = [{ text: "Load Name", value: "loadName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getLoadTableData(metric) {
      return this.resourcesItems.load.map((loadItem) => {
        const values = this.filteredOutputs.map((timePoint) => ({
          time: timePoint.datetime,
          value: timePoint.value.load?.[loadItem.value]?.[metric],
        }));
        return {
          loadName: loadItem.text,
          values,
        };
      });
    },

    // Generator table methods
    getGenTableHeaders() {
      const headers = [{ text: "Generator Name", value: "genName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getGenTableData(metric) {
      return this.resourcesItems.gen.map((genItem) => {
        const values = this.filteredOutputs.map((timePoint) => {
          let value = timePoint.value.gen?.[genItem.value]?.[metric];
          if (metric === "power_factor") {
            const p_mw = timePoint.value.gen?.[genItem.value]?.p_mw || 0;
            const q_mvar = timePoint.value.gen?.[genItem.value]?.q_mvar || 0;
            const apparent = Math.sqrt(p_mw * p_mw + q_mvar * q_mvar);
            value = apparent > 0 ? Math.abs(p_mw) / apparent : 0;
          }
          return {
            time: timePoint.datetime,
            value,
          };
        });
        return {
          genName: genItem.text,
          values,
        };
      });
    },

    // Static Generator table methods
    getSgenTableHeaders() {
      const headers = [{ text: "Static Generator Name", value: "sgenName", sortable: false }];
      this.filteredOutputs.forEach((timePoint) => {
        headers.push({
          text: this.formatTime(timePoint.datetime),
          value: timePoint.datetime,
          sortable: false,
          class: "text-center",
        });
      });
      return headers;
    },

    getSgenTableData(metric) {
      return this.resourcesItems.sgen.map((sgenItem) => {
        const values = this.filteredOutputs.map((timePoint) => ({
          time: timePoint.datetime,
          value: timePoint.value.sgen?.[sgenItem.value]?.[metric],
        }));
        return {
          sgenName: sgenItem.text,
          values,
        };
      });
    },

    exportToCSV() {
      const zip = new Zip();

      // Bus data
      if (this.resourcesItems.bus.length > 0) {
        this.busMetrics.forEach((metric) => {
          const busData = this.getBusTableData(metric.key);
          if (busData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(busData), { newline: "\n" });
            zip.file(`Node ${metric.name}.csv`, csvData);
          }
        });
      }

      // Line data
      if (this.resourcesItems.line.length > 0) {
        this.lineMetrics.forEach((metric) => {
          const lineData = this.getLineTableData(metric.key);
          if (lineData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(lineData), { newline: "\n" });
            zip.file(`Branch ${metric.name}.csv`, csvData);
          }
        });
      }

      // 2-Winding Transformer data
      if (this.resourcesItems.trafo.length > 0) {
        this.trafoMetrics.forEach((metric) => {
          const trafoData = this.getTrafoTableData(metric.key);
          if (trafoData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(trafoData), {
              newline: "\n",
            });
            zip.file(`2-winding Trafo ${metric.name}.csv`, csvData);
          }
        });
      }

      // 3-Winding Transformer data
      if (this.resourcesItems.trafo3w.length > 0) {
        this.trafo3wMetrics.forEach((metric) => {
          const trafo3wData = this.getTrafo3wTableData(metric.key);
          if (trafo3wData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(trafo3wData), {
              newline: "\n",
            });
            zip.file(`3-winding Trafo ${metric.name}.csv`, csvData);
          }
        });
      }

      // Load data
      if (this.resourcesItems.load.length > 0) {
        this.loadMetrics.forEach((metric) => {
          const loadData = this.getLoadTableData(metric.key);
          if (loadData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(loadData), { newline: "\n" });
            zip.file(`Demand ${metric.name}.csv`, csvData);
          }
        });
      }

      // Generator data
      if (this.resourcesItems.gen.length > 0) {
        this.genMetrics.forEach((metric) => {
          const genData = this.getGenTableData(metric.key);
          if (genData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(genData), { newline: "\n" });
            zip.file(`PV Generator ${metric.name}.csv`, csvData);
          }
        });
      }

      // Static Generator data
      if (this.resourcesItems.sgen.length > 0) {
        this.sgenMetrics.forEach((metric) => {
          const sgenData = this.getSgenTableData(metric.key);
          if (sgenData.length > 0) {
            const csvData = Papa.unparse(this.convertTableDataToCSV(sgenData), { newline: "\n" });
            zip.file(`PQ Generator ${metric.name}.csv`, csvData);
          }
        });
      }

      // Contingency analysis data
      // TODO: turn into single excel file instead of multiple CSVs
      const contingencies = this.outputs.find((obj) => obj.contingencies)?.contingencies;
      if (contingencies && contingencies.length > 0) {
        const summaries = contingencies.map((v, i) => {
          return { "Contingency ID": `C-${i}`, ...v.summary };
        });
        const summaryCSV = Papa.unparse(summaries, { newline: "\n" });
        zip.file("Contingency summary.csv", summaryCSV);

        const detailSheets = ["bus", "line", "trafo", "gen"];
        detailSheets.forEach((sheetName) => {
          const sheetData = contingencies.flatMap((v, i) => {
            if (!v[sheetName]) {
              return [];
            }
            return v[sheetName].map((dataPoint) => {
              return { "Contingency ID": `C-${i}`, ...dataPoint };
            });
          });
          const sheetCSV = Papa.unparse(sheetData, { newline: "\n" });
          zip.file(`Contingency ${sheetName}.csv`, sheetCSV);
        });
      } else if (contingencies && contingencies.length === 0) {
        zip.file(`No elements with contingency simulations enabled!`, "");
      }

      // Generate and download the zip file
      zip.generateAsync({ type: "blob" }).then((blob) => {
        const fileName = `${this.currentCase.name}_power_flow_data.zip`;
        download(blob, fileName);
      });
    },

    convertTableDataToCSV(tableData) {
      if (!tableData || tableData.length === 0) return [];

      const timeHeaders = this.filteredOutputs.map((o) => this.formatTime(o.datetime));
      const headers = ["Name", ...timeHeaders];

      // Create data rows
      const rows = tableData.map((item) => {
        const row = [Object.values(item)[0]]; // Element name
        item.values.forEach((timeValue) => {
          row.push(this.formatTableValue(timeValue.value));
        });
        return row;
      });

      return [headers, ...rows];
    },
  },
};
</script>

<style scoped>
.v-tabs {
  margin-bottom: 16px;
}

.v-row {
  margin-bottom: 24px;
}

.v-col h3,
.v-col h4 {
  margin-bottom: 16px;
  color: #1976d2;
}
</style>
